import {DotProgress} from '@wandb/components';
import React, {useCallback, useEffect} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {useDebounceState} from '../../../util/hooks';
import {maybePluralize} from '../../../util/uihelpers';
import {useQuickAddKeys} from '../../WorkspaceDrawer/PanelsQuickAdd/hooks/useQuickAddKeys';
import {usePanelsQuickAddContext} from '../../WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {usePanelBankSearchResults} from './PanelBankSearchResultsContext';
import {usePanelSearchUpdatePending} from './useIsPanelSearchUpdatePending';

const QUICK_ADD_COUNT_DEBOUNCE_MS = 250;

export const SearchResultCounts = () => {
  const {debouncedSearchQuery} = usePanelSearchDebouncedContext();

  // compute number of matching panels/sections
  const {
    numPanelsHidden,
    numPanelsMatched,
    numSectionsMatched,
    visibleSections,
  } = usePanelBankSearchResults();

  const [, quickAddSearchQuery, setQuickAddSearchQuery] = useDebounceState(
    debouncedSearchQuery,
    QUICK_ADD_COUNT_DEBOUNCE_MS
  );
  useEffect(() => {
    setQuickAddSearchQuery(debouncedSearchQuery);
  }, [debouncedSearchQuery, setQuickAddSearchQuery]);

  // compute number of matching keys that can be added as panels.
  // only relevant with a positive search query, so skip the work for
  // hide-only filters (avoids walking all keys/panels for a discarded count)
  const hasSearchQuery = debouncedSearchQuery.length > 0;
  const {keysWithNoPanels} = useQuickAddKeys(
    quickAddSearchQuery,
    undefined,
    hasSearchQuery
  );
  const isQuickAddCountSettling = quickAddSearchQuery !== debouncedSearchQuery;
  const numAvailableKeys =
    hasSearchQuery && !isQuickAddCountSettling ? keysWithNoPanels.length : 0;
  const {openQuickAdd} = usePanelsQuickAddContext();
  const openGlobalQuickAddWithSearch = useCallback(
    () =>
      openQuickAdd({
        location: 'Panel bank search',
        searchStr: debouncedSearchQuery,
      }),
    [debouncedSearchQuery, openQuickAdd]
  );

  // While updated search results are being fetched, show loading indicator in place of counts
  // When there are no visible sections, the empty panel bank area shows a larger loader and
  // this smaller one should be hidden
  const isPanelSearchUpdatePending = usePanelSearchUpdatePending();
  if (isPanelSearchUpdatePending && visibleSections.length > 0) {
    return (
      <div className="flex items-center gap-8 text-wb-text-tertiary">
        <DotProgress size="sm" aria-label="Loading updated search results" />
        Searching
      </div>
    );
  }

  // determine if counts should be displayed
  const hasPanelsOrSections = numPanelsMatched > 0 || numSectionsMatched > 0;
  const hasHiddenPanels = numPanelsHidden > 0;
  const hasData =
    hasPanelsOrSections || numAvailableKeys > 0 || hasHiddenPanels;
  if (!hasData) {
    return null;
  }

  return (
    <p className="text-wb-text-tertiary">
      {hasPanelsOrSections && (
        <>
          {numPanelsMatched > 0 && (
            <span className="font-semibold">
              {maybePluralize(numPanelsMatched, 'panel', 's', true)}
            </span>
          )}
          {numPanelsMatched > 0 && numSectionsMatched > 0 && ' and '}
          {numSectionsMatched > 0 && (
            <span className="font-semibold">
              {maybePluralize(numSectionsMatched, 'section', 's', true)}
            </span>
          )}
          {' match your search and run filters'}
        </>
      )}
      {numAvailableKeys > 0 && (
        <>
          {hasPanelsOrSections && ', '}
          <span
            role="button"
            className="cursor-pointer font-semibold text-wb-text-link"
            onClick={openGlobalQuickAddWithSearch}>
            {maybePluralize(numAvailableKeys, 'available key', 's', true)}
          </span>
        </>
      )}
      {hasHiddenPanels && (
        <>
          {(hasPanelsOrSections || numAvailableKeys > 0) && (
            <span className="font-semibold"> · </span>
          )}
          <span className="font-semibold">
            {maybePluralize(numPanelsHidden, 'panel', 's', true)}
          </span>
          {' hidden'}
        </>
      )}
    </p>
  );
};
